import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Clock, Users, Check, ChevronDown, ChevronUp } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/uploads')) return `${BASE_URL}/api${url}`;
  return url;
};

const ExperiencesPage = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPackage, setExpandedPackage] = useState(null);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await axios.get(`${API}/packages`);
        setPackages(response.data);
      } catch (error) {
        console.error("Error fetching packages:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  const toggleExpand = (pkgId) => {
    setExpandedPackage(expandedPackage === pkgId ? null : pkgId);
  };

  // Payment terms that apply to all packages
  const paymentTerms = [
    "A 60% retainer is required to secure your booking",
    "Remaining balance is due 3 days prior to the event",
    "Retainers are non-refundable but transferable if rescheduled within 6 months"
  ];

  return (
    <main className="pt-20" data-testid="experiences-page">
      {/* Hero */}
      <section className="section-padding bg-[#050505]">
        <div className="container-luxury">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4">
              Our Services
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-['Playfair_Display'] font-bold mb-6 text-[#F5F5F0]">
              Experience Packages
            </h1>
            <p className="text-[#A1A1AA] text-lg leading-relaxed">
              From intimate gatherings to grand celebrations, we have the perfect package 
              to make your event unforgettable. Each experience is customizable to match your vision.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Packages Grid */}
      <section className="section-padding bg-[#0F0F0F]" data-testid="packages-section">
        <div className="container-luxury">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="spinner" />
            </div>
          ) : (
            <div className="space-y-8">
              {packages.map((pkg, index) => (
                <motion.div
                  key={pkg.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  className="luxury-card overflow-hidden"
                  data-testid={`package-${pkg.id}`}
                >
                  {/* Package Image (if available) */}
                  {pkg.image && !expandedPackage && (
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={getImageUrl(pkg.image)} 
                        alt={pkg.name}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-transparent to-transparent" />
                    </div>
                  )}
                  
                  {/* Package Header */}
                  <div 
                    className="p-6 md:p-8 cursor-pointer hover:bg-[rgba(212,175,55,0.03)] transition-colors"
                    onClick={() => toggleExpand(pkg.id)}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <h3 className="font-['Playfair_Display'] text-xl md:text-2xl font-semibold text-[#F5F5F0]">
                            {pkg.name}
                          </h3>
                          <div className="flex items-center gap-4 text-sm text-[#A1A1AA]">
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4 text-[#D4AF37]" />
                              {pkg.duration}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-4 h-4 text-[#D4AF37]" />
                              {pkg.guests}
                            </span>
                          </div>
                        </div>
                        <p className="text-[#A1A1AA] italic font-['Cormorant_Garamond'] text-lg">
                          {pkg.tagline}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <span className="text-2xl md:text-3xl font-['Playfair_Display'] font-bold gold-text whitespace-nowrap">
                          {pkg.price_display}
                        </span>
                        <button 
                          className="p-2 border border-[rgba(212,175,55,0.3)] hover:border-[#D4AF37] transition-colors"
                          aria-label={expandedPackage === pkg.id ? "Collapse" : "Expand"}
                        >
                          {expandedPackage === pkg.id ? (
                            <ChevronUp className="w-5 h-5 text-[#D4AF37]" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-[#D4AF37]" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {expandedPackage === pkg.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="border-t border-[rgba(212,175,55,0.15)] p-6 md:p-8 bg-[#0a0a0a]"
                    >
                      <div className="grid md:grid-cols-2 gap-8">
                        {/* Left Column */}
                        <div>
                          {/* Show image in expanded view */}
                          {pkg.image && (
                            <div className="mb-6 overflow-hidden border border-[rgba(212,175,55,0.2)]">
                              <img 
                                src={getImageUrl(pkg.image)} 
                                alt={pkg.name}
                                className="w-full h-48 object-cover"
                              />
                            </div>
                          )}
                          
                          <p className="text-[#A1A1AA] leading-relaxed mb-6">
                            {pkg.description}
                          </p>
                          
                          <h4 className="text-[#D4AF37] uppercase tracking-widest text-sm mb-4">
                            What's Included
                          </h4>
                          <ul className="space-y-3 mb-6">
                            {pkg.includes.map((item, i) => (
                              <li key={i} className="flex items-start gap-3 text-[#A1A1AA]">
                                <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>

                          <div className="p-4 border border-[rgba(212,175,55,0.2)] bg-[#121212]">
                            <h4 className="text-[#F5F5F0] font-semibold mb-2">Why Choose This Package?</h4>
                            <p className="text-[#A1A1AA] text-sm">{pkg.why_choose}</p>
                          </div>
                        </div>

                        {/* Right Column */}
                        <div>
                          <h4 className="text-[#D4AF37] uppercase tracking-widest text-sm mb-4">
                            Retainer & Payment Terms
                          </h4>
                          <ul className="space-y-2 mb-6">
                            {paymentTerms.map((term, i) => (
                              <li key={i} className="flex items-start gap-2 text-[#A1A1AA] text-sm">
                                <span className="text-[#D4AF37]">•</span>
                                <span>{term}</span>
                              </li>
                            ))}
                          </ul>

                          <div className="p-4 bg-[#121212] border border-[rgba(212,175,55,0.15)] mb-6">
                            <p className="text-[#52525B] text-xs leading-relaxed">
                              <strong className="text-[#A1A1AA]">NOTE:</strong> All pricing is subject to change based on location, 
                              the complexity of the event, customizations, and additional requirements. 
                              We offer personalized quotes for each event to ensure that the experience meets your expectations.
                            </p>
                          </div>

                          <p className="text-[#A1A1AA] text-sm italic mb-6">
                            *Final pricing is customized based on guest count, service hours, location, and add-ons.
                          </p>

                          <div className="flex flex-col sm:flex-row gap-4">
                            {pkg.price > 0 ? (
                              <Link
                                to={`/booking?package=${pkg.id}`}
                                className="btn-primary text-center"
                                data-testid={`book-${pkg.id}-btn`}
                              >
                                Book This Package
                              </Link>
                            ) : (
                              <Link
                                to="/contact"
                                className="btn-primary text-center"
                                data-testid={`contact-${pkg.id}-btn`}
                              >
                                Contact Us for Quote
                              </Link>
                            )}
                            <Link
                              to="/contact"
                              className="btn-secondary text-center"
                              data-testid={`inquire-${pkg.id}-btn`}
                            >
                              Request Consultation
                            </Link>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Custom Experiences Section */}
      <section className="section-padding bg-[#050505]" data-testid="custom-section">
        <div className="container-luxury">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[#D4AF37] uppercase tracking-[0.2em] text-sm mb-4">
                Custom Experiences
              </p>
              <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] font-bold mb-6 text-[#F5F5F0]">
                Don't See What You Need?
              </h2>
              <div className="gold-divider" />
              <p className="text-[#A1A1AA] leading-relaxed mb-6">
                We specialize in creating custom beverage experiences tailored to your unique vision. 
                Whether you're planning a themed event, need specific dietary accommodations, or have 
                a creative concept in mind, our team is ready to bring it to life.
              </p>
              <p className="text-[#A1A1AA] leading-relaxed mb-8">
                Contact us for a consultation, and we'll be happy to customize a package that fits 
                your event perfectly.
              </p>
              <Link 
                to="/contact" 
                className="btn-primary inline-flex items-center gap-2"
                data-testid="custom-inquiry-btn"
              >
                Get Custom Quote
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="aspect-square overflow-hidden"
            >
              <img
                src="https://images.unsplash.com/photo-1767016452826-815c7b4765ed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxlbGVnYW50JTIwcGVvcGxlJTIwcGFydHklMjB0b2FzdGluZyUyMGNoYW1wYWduZSUyMG5pZ2h0fGVufDB8fHx8MTc3MjM1MzYwOXww&ixlib=rb-4.1.0&q=85"
                alt="Custom event service"
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding-sm bg-[#0F0F0F] border-t border-[rgba(212,175,55,0.15)]">
        <div className="container-luxury text-center">
          <h2 className="text-2xl sm:text-3xl font-['Playfair_Display'] font-bold text-[#F5F5F0] mb-4">
            Ready to Book Your Experience?
          </h2>
          <p className="text-[#A1A1AA] mb-8 max-w-xl mx-auto">
            Select your preferred package and let us create an unforgettable event for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/booking" 
              className="btn-primary inline-flex items-center justify-center gap-2"
              data-testid="experiences-cta-btn"
            >
              Start Booking
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link 
              to="/contact" 
              className="btn-secondary"
              data-testid="experiences-contact-btn"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ExperiencesPage;
